import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SendMessagePayload } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

interface UseSendMessageOptions {
  supportTicketId: SupportTicketId;
}

/**
 * Hook to send a message.
 * Optimistically marks the draft as SENDING in the collection so the
 * UI immediately reflects the in-flight state. The real message arrives via
 * the SSE messageCreated event, which triggers a collection invalidation.
 */
export function useSendMessage({ supportTicketId }: UseSendMessageOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const [isSending, setIsSending] = useState(false);

  const sendSupportTicketMessage = async (input: SendMessagePayload) => {
    setIsSending(true);
    try {
      collections.drafts.utils.writeUpdate({
        id: input.draftId,
        status: "SENDING" as const,
      });

      const client = effectApi.getClient();
      const sendCall = client.supportTicket.sendSupportTicketMessage({
        path: { id: supportTicketId },
        payload: input,
      });
      await effectApi.runPromise(catchApiErrors(sendCall));
    } finally {
      setIsSending(false);
    }
  };

  return { sendSupportTicketMessage, isSending };
}
